import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Scroll, BookMarked, Map, Flame, Trophy, Crown, Feather, KeyRound, Snowflake, Calendar, ExternalLink, Award, Coins, Sword, MessageCircle } from 'lucide-react';

function StatBox({ icon: Icon, label, value, accent }) {
  return (
    <div className="codex-card" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: accent || 'var(--codex-gold-light)' }}>
        <Icon size={16} />
        <span className="codex-eyebrow" style={{ color: 'inherit' }}>{label}</span>
      </div>
      <div className="codex-display" style={{ fontSize: 28, color: 'var(--codex-parchment)' }}>{value}</div>
    </div>
  );
}

export default function CodexDashboard({ state }) {
  const navigate = useNavigate();
  const p = state.progress || {};
  const todayDone = !!p.today_completed && p.last_active_date === state.today_uk_date;

  // Pull the user's next confirmed appointment for the "Next Session" widget
  const [nextSession, setNextSession] = useState(null);
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user_data') || 'null');
    const token = localStorage.getItem('access_token');
    if (!userData?.id || !token) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/appointments/user/${userData.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.ok ? r.json() : [])
      .then((apts) => {
        const todayStr = new Date().toISOString().slice(0, 10);
        const upcoming = (apts || [])
          .filter((a) => ['confirmed', 'pending'].includes(a.status) && a.date >= todayStr)
          .sort((a, b) => `${a.date}T${a.time}`.localeCompare(`${b.date}T${b.time}`));
        setNextSession(upcoming[0] || null);
      })
      .catch(() => {});
  }, []);

  return (
    <div style={{ paddingTop: 8 }} data-testid="codex-dashboard">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} style={{ marginBottom: 18 }}>
        <div className="codex-eyebrow">{p.title?.toUpperCase()} · LEVEL {p.level}</div>
        <h1 className="codex-h1" style={{ marginTop: 6 }}>
          Welcome back, <span style={{ color: 'var(--codex-gold-light)' }}>{p.avatar ? p.avatar[0].toUpperCase() + p.avatar.slice(1) : 'Scribe'}</span>.
        </h1>
        <p style={{ opacity: 0.75, marginTop: 8 }}>
          {todayDone
            ? 'Your scroll for today is sealed. Walk on, beloved one.'
            : 'A new scroll awaits you, faithful one. Open it when you are ready.'}
        </p>
      </motion.div>

      {/* Hero card */}
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.1 }}
        className="codex-card-gold codex-card"
        style={{
          padding: 32,
          marginBottom: 24,
          position: 'relative',
          overflow: 'hidden',
          cursor: 'pointer',
        }}
        onClick={() => navigate('/codex/scroll')}
        data-testid="codex-today-scroll-cta"
      >
        <motion.div
          aria-hidden
          animate={{ rotate: 360 }}
          transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
          style={{
            position: 'absolute',
            right: -120,
            top: -120,
            width: 320,
            height: 320,
            borderRadius: '50%',
            border: '1px dashed rgba(212,175,55,0.3)',
          }}
        />
        <div className="codex-eyebrow" style={{ color: 'var(--codex-gold)' }}>TODAY'S SCROLL</div>
        <div className="codex-display" style={{ fontSize: 32, marginTop: 8, color: 'var(--codex-gold-light)' }}>
          {todayDone ? '✦ Scroll Sealed' : 'Open the Scroll'}
        </div>
        <div style={{ fontSize: 14, opacity: 0.85, marginTop: 8, maxWidth: 480 }}>
          {todayDone
            ? "You decoded all three layers today. Return tomorrow at 6 AM for the next mystery."
            : 'Decode the literal, cultural and prophetic layers — earn XP and grow your Codex.'}
        </div>
        <button
          className="codex-btn codex-btn-primary"
          style={{ marginTop: 20 }}
          onClick={(e) => { e.stopPropagation(); navigate('/codex/scroll'); }}
        >
          <Scroll size={16} /> {todayDone ? 'Revisit' : 'Begin Decoding'}
        </button>
      </motion.div>

      {/* Bento grid stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
        <StatBox icon={Flame} label="Streak" value={`${p.streak_days || 0} days`} accent="var(--codex-orange)" />
        <StatBox icon={Trophy} label="XP" value={p.xp || 0} />
        <StatBox icon={BookMarked} label="Mastered Words" value={(p.completed_words || []).length} />
        <StatBox icon={Snowflake} label="Streak Shields" value={p.streak_freezes || 0} accent="#7dd3fc" />
        <StatBox icon={Crown} label="Title" value={p.title || 'Apprentice'} />
      </div>

      {/* Next Session widget — bridges to academy appointments */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.18 }}
        className="codex-card"
        style={{
          padding: 18,
          marginBottom: 18,
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          flexWrap: 'wrap',
          borderColor: nextSession ? 'rgba(34,197,94,0.35)' : undefined,
          background: nextSession ? 'rgba(34,197,94,0.06)' : undefined,
        }}
        data-testid="codex-next-session"
      >
        <Calendar size={22} color={nextSession ? '#22c55e' : 'var(--codex-gold-light)'} />
        <div style={{ flex: 1, minWidth: 200 }}>
          <div className="codex-eyebrow">NEXT 1-1 SESSION</div>
          {nextSession ? (
            <div style={{ marginTop: 4, fontSize: 14 }}>
              <strong style={{ color: 'var(--codex-gold-light)' }}>
                {new Date(nextSession.date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short', timeZone: 'Europe/London' })}
              </strong>
              {' '}· {nextSession.time} (UK) · {nextSession.status}
              {nextSession.topic && <div style={{ opacity: 0.75, fontSize: 12, marginTop: 2 }}>"{nextSession.topic}"</div>}
            </div>
          ) : (
            <div style={{ marginTop: 4, fontSize: 13, opacity: 0.8 }}>
              No session scheduled. Book one to deepen your journey.
            </div>
          )}
        </div>
        <a
          href="/appointments"
          target="_blank"
          rel="noopener noreferrer"
          className="codex-btn codex-btn-ghost"
          data-testid="codex-book-session"
        >
          <ExternalLink size={14} /> {nextSession ? 'Manage' : 'Book a session'}
        </a>
      </motion.div>

      {/* Action tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
        {[
          { to: '/codex/missions', icon: Sword, label: 'Sacred Missions', desc: 'Daily and weekly charges to keep you walking.' },
          { to: '/codex/codex-book', icon: BookMarked, label: 'My Codex', desc: 'Review every word you have mastered.' },
          { to: '/codex/chambers', icon: KeyRound, label: 'Mystery Vault', desc: 'Seven prophetic chambers — unlock by mastering scrolls.' },
          { to: '/codex/journal', icon: Feather, label: 'Reflection Journal', desc: 'What is the Spirit saying to you?' },
          { to: '/codex/achievements', icon: Award, label: 'Hall of Seals', desc: 'Every milestone is a stone of remembrance.' },
          { to: '/codex/chat', icon: MessageCircle, label: 'Live Chat', desc: 'Real-time conversations with your mentor.' },
          { to: '/codex/treasury', icon: Coins, label: 'Sacred Treasury', desc: 'Spend your Shekels on robes, symbols, and titles.' },
          { to: '/codex/journey', icon: Map, label: 'My Journey', desc: 'Your personal pilgrimage at a glance.' },
        ].map((tile, i) => (
          <motion.div
            key={tile.to}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 + i * 0.08 }}
            whileHover={{ y: -3 }}
            onClick={() => navigate(tile.to)}
            className="codex-card"
            style={{ padding: 22, cursor: 'pointer' }}
            data-testid={`codex-tile-${tile.label.replace(/\s/g, '').toLowerCase()}`}
          >
            <tile.icon size={22} color="var(--codex-gold-light)" />
            <div className="codex-display" style={{ marginTop: 10, fontSize: 20, color: 'var(--codex-parchment)' }}>{tile.label}</div>
            <div style={{ fontSize: 13, opacity: 0.75, marginTop: 4 }}>{tile.desc}</div>
          </motion.div>
        ))}
      </div>

      {/* Yesterday vs today (placeholder for now) */}
      <div className="codex-card" style={{ padding: 22, marginTop: 24 }}>
        <div className="codex-eyebrow">YOU vs YESTERDAY</div>
        <div style={{ marginTop: 10, opacity: 0.85, lineHeight: 1.7 }}>
          {todayDone ? (
            <>You completed today's scroll. <strong style={{ color: 'var(--codex-gold-light)' }}>Yesterday-you would be proud.</strong></>
          ) : (
            <>The only competition is yourself. <strong style={{ color: 'var(--codex-gold-light)' }}>Show up today and you have already won.</strong></>
          )}
        </div>
      </div>
    </div>
  );
}
